import { reduceBankHolidays } from './BankHolidayReducer';
import moment from 'moment';
import { BankHolidays } from '../models/APIModels';
import { Address } from '../components/AddressLookup';

const unavailableDates2025_2026 = [
  new Date('2025-12-22'), new Date('2025-12-23'), new Date('2025-12-24'),
  new Date('2025-12-29'), new Date('2025-12-30'), new Date('2025-12-31'),
  new Date('2026-01-02'), new Date('2026-01-05'),
].map((d) => { d.setHours(0, 0, 0, 0); return d; });

function copyBlackoutDates(): Date[] {
  return unavailableDates2025_2026.map((d) => new Date(d.getTime()));
}

function buildNonWorkingDates(
  bankHolidays: BankHolidays,
  address: Address | null,
): Date[] {
  const dates: Date[] = copyBlackoutDates();
  const events = reduceBankHolidays(bankHolidays, address) || [];
  for (const event of events) {
    const d = new Date(event.date);
    d.setHours(0, 0, 0, 0);
    dates.push(d);
  }
  return dates;
}

function isNonWorkingDay(date: Date, nonWorkingDates: Date[]): boolean {
  const dow = date.getDay();
  if (dow === 0 || dow === 6) return true;
  const time = date.getTime();
  return nonWorkingDates.some((d) => d.getTime() === time);
}

export function addWorkingDays(from: Date, days: number, nonWorkingDates: Date[]): Date {
  const result = new Date(from);
  result.setHours(0, 0, 0, 0);
  let added = 0;
  while (added < days) {
    result.setDate(result.getDate() + 1);
    result.setHours(0, 0, 0, 0);
    if (!isNonWorkingDay(result, nonWorkingDates)) {
      added++;
    }
  }
  return result;
}

/**
 * Returns true when `targetDate` is within `workingDays` working days
 * of `currentDate` (inclusive of the boundary day), accounting for
 * weekends, UK bank holidays, and the Christmas/New Year blackout dates.
 */
export function isWithinWorkingDays(
  targetDate: number | Date,
  currentDate: Date,
  workingDays: number,
  bankHolidays: BankHolidays | null,
  address: Address | null,
): boolean {
  const target = new Date(targetDate);
  target.setHours(0, 0, 0, 0);

  const nonWorkingDates = bankHolidays
    ? buildNonWorkingDates(bankHolidays, address)
    : copyBlackoutDates();
  const cutoff = addWorkingDays(currentDate, workingDays, nonWorkingDates);
  return target <= cutoff;
}

export function populateDisabledDays(
  bankHolidays: BankHolidays,
  address: Address | null,
  currentDate: Date,
  initialDisabledDays: any[] | null,
  minDaysBuffer?: number,
) {
  let disabledDays = [];

  let datesToInclude = reduceBankHolidays(bankHolidays!!, address);

  for (const bankHoliday of datesToInclude) {
    const bankHolidayDate = new Date(bankHoliday.date);
    if (bankHolidayDate >= currentDate) {
      bankHolidayDate.setHours(0, 0, 0, 0);
      disabledDays.push(bankHolidayDate);
    }
  }

  const dayOfWeek = currentDate.getDay();
  const isAfterCutOff = currentDate.getHours() >= 11;
  const hasInitialDisabledDays = !!(initialDisabledDays && initialDisabledDays.length > 0);

  let numberOfDays;

  if (minDaysBuffer !== undefined) {
    // Removals/Mini tiers carry their own explicit buffer
    numberOfDays = minDaysBuffer;
  } else if (dayOfWeek === 0 || isAfterCutOff) {
    // Sunday is fully non-working — earliest collection is always Wednesday
    // (Sun + 3), so the 11am cut-off doesn't apply on Sunday.
    numberOfDays = hasInitialDisabledDays ? 4 : 3;
  } else {
    numberOfDays = hasInitialDisabledDays ? 3 : 2;
  }

  // Thu/Fri/Sat/Wed-after-11 bookings spill into Sat + Sun (+2).
  // Sunday already lands on Wed via the base above, so skip the spillover bump.
  if (dayOfWeek !== 0 && dayOfWeek + numberOfDays >= 6) {
    numberOfDays += 2;
  }

  // If any of the days between now and the nearest collection date are bank holidays, add an extra disabled day.
  for (let i = 1; i < numberOfDays; i++) {
    let date = new Date(currentDate);
    date.setDate(date.getDate() + i);
    date.setHours(0, 0, 0, 0);
    if (
      disabledDays.find(
        (disabledDate) => disabledDate.getTime() === date.getTime(),
      )
    ) {
      numberOfDays += 1;
    }
  }

  const dateBefore = moment(currentDate.getTime())
    .add(numberOfDays, 'days')
    .toDate();
  dateBefore.setHours(0, 0, 0, 0);

  disabledDays.push({ daysOfWeek: [0, 6] });
  disabledDays.push({ before: dateBefore });

  disabledDays.push({
    from: new Date('2024-12-21'),
    to: new Date('2025-01-05'),
  });

  disabledDays.push(...copyBlackoutDates());

  if (initialDisabledDays) {
    disabledDays.push(...initialDisabledDays);
  }

  return disabledDays;
}
